export const beforeItems = [
  'You memorize laboratory values without fully understanding how the concepts relate.',
  'You struggle to distinguish respiratory from metabolic acid-base disorders.',
  'You repeatedly search for explanations of compensation principles.',
  'You spend too much time moving between textbooks and reference materials.',
  'You complete practice questions without a consistent framework for reviewing your reasoning.',
  'You find it difficult to organize everything you’ve learned into one coherent study process.',
];

export const afterItems = [
  'You have six organized volumes covering complementary ABG learning topics.',
  'You can review pH, PaCO₂, and HCO₃⁻ using structured educational references.',
  'You can compare respiratory and metabolic disorders through dedicated tables.',
  'You can study compensation concepts through organized explanations and exercises.',
  'You can practice your reasoning with simulated educational cases.',
  'You have practical worksheets and quick-reference materials to support your study sessions.',
];

export const painPoints = [
  {
    icon: 'connect',
    title: 'You struggle to connect pH, PaCO₂, and HCO₃⁻.',
    text: 'You understand that these measurements matter, but you want a clearer way to study their relationships and organize your interpretation of different acid-base patterns.',
  },
  {
    icon: 'search',
    title: 'You spend too much time searching for explanations.',
    text: 'You have textbooks, lecture notes, saved PDFs, and reference charts scattered across different places. You want your essential study materials organized in one collection.',
  },
  {
    icon: 'compare',
    title: 'Respiratory and metabolic disorders still confuse you.',
    text: 'You want to study the differences between these disorders through clear explanations, comparison tables, and guided exercises.',
  },
  {
    icon: 'scale',
    title: 'Compensation concepts feel overwhelming.',
    text: 'You want a more organized way to study physiological compensation, understand its underlying principles, and recognize the limitations of simplified rules.',
  },
  {
    icon: 'practice',
    title: 'You want to practice instead of simply memorizing.',
    text: 'You need educational exercises that encourage you to organize information, explain your reasoning, and identify concepts that deserve further study.',
  },
  {
    icon: 'repeat',
    title: 'You repeatedly review the same topics.',
    text: 'You want reference sheets, checklists, and structured study materials that make it easier to revisit important concepts without starting from scratch.',
  },
];

export const tools = [
  {
    icon: 'planner',
    title: 'The ABG Study Session Planner',
    text: 'A reusable worksheet for selecting a topic, identifying learning objectives, reviewing essential concepts, and recording questions for further study.',
  },
  {
    icon: 'matrix',
    title: 'The Acid-Base Comparison Matrix',
    text: 'A visual educational reference for comparing respiratory and metabolic acid-base concepts and their associated physiological relationships.',
  },
  {
    icon: 'worksheet',
    title: 'The ABG Interpretation Practice Worksheet',
    text: 'A structured worksheet for organizing pH, PaCO₂, HCO₃⁻, contextual information, and your reasoning during educational exercises.',
  },
  {
    icon: 'brain',
    title: 'The Compensation Concepts Reference Sheet',
    text: 'An organized summary of compensation principles, related terminology, and important interpretation limitations.',
  },
  {
    icon: 'checklist',
    title: 'The ABG Knowledge Review Checklist',
    text: 'A topic-based checklist to help you identify which concepts you’ve reviewed and which ones you want to revisit.',
  },
];

export const testimonials = [
  {
    name: 'Daniela R.',
    role: 'Nursing student',
    country: 'United States',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/44dcb14a-4ba6-4113-8421-08b8e7b5a592.jpg',
    quote:
      'I was always jumping between lecture slides and random charts. Having pH, PaCO₂ and HCO₃⁻ explained in one place, in the same order every time, finally made my study sessions feel organized instead of chaotic. The comparison tables in Volume II are the pages I open the most.',
  },
  {
    name: 'Keisha M.',
    role: 'ICU nurse',
    country: 'United States',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/e65a352d-1961-4b8c-a223-a130bb5816bf.jpg',
    quote:
      'The compensation reference sheet is exactly the refresher I wanted. Clear, concise, and honest about where the simplified rules stop working.',
  },
  {
    name: 'Marcus T.',
    role: 'New graduate nurse',
    country: 'Canada',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/3e89fd13-18e3-4b82-b7af-923aeb2b21ef.jpg',
    quote:
      'The workbook’s step-by-step sequence changed how I review practice questions. I now write out my reasoning instead of guessing.',
  },
  {
    name: 'Grace L.',
    role: 'Med-surg nurse',
    country: 'Philippines',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/ba7b536d-3f51-428c-b9de-a9d36bd3c141.jpg',
    quote:
      'The pitfalls library was eye-opening. It showed me why one value alone never tells the full story.',
  },
  {
    name: 'Emily S.',
    role: 'Nursing student',
    country: 'United Kingdom',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/94da438a-abbc-4bd6-b8dc-0c7e6ece0575.jpg',
    quote:
      'Fifteen minutes with the study planner each evening is realistic for me, and the checklists show exactly what to revisit next.',
  },
];

export const faqs = [
  {
    q: 'How will I receive the material?',
    a: 'After your payment is confirmed, you’ll receive instructions for accessing the digital files through the delivery method configured at checkout.',
  },
  {
    q: 'Is this collection suitable for nursing students?',
    a: 'Yes. The material is designed to support the study of foundational ABG concepts, acid-base physiology, interpretation principles, and educational exercises. It does not replace formal nursing education, instructor guidance, or supervised clinical training.',
  },
  {
    q: 'Is it suitable for experienced critical care nurses?',
    a: 'Experienced professionals may use the collection to review selected concepts and organize their educational reference materials. It is not a substitute for professional continuing education or current clinical guidance.',
  },
  {
    q: 'Does the roadmap cover every possible ABG situation?',
    a: 'No. The collection focuses on foundational concepts, common acid-base disorders, compensation principles, reference tools, and simulated educational cases. Complex clinical situations may require additional study, current evidence, and professional evaluation.',
  },
  {
    q: 'Can I use this resource to make clinical decisions?',
    a: 'No. This product is intended for educational purposes only. It does not establish validated clinical decision algorithms or replace patient-specific assessment, professional judgment, supervised training, or institutional protocols.',
  },
  {
    q: 'Do I need to be an ICU specialist?',
    a: 'No. The collection is intended for nursing students, nurses, and other healthcare learners who want to study ABG concepts. You should use the material according to your background and training.',
  },
  {
    q: 'Are the files downloadable?',
    a: 'The product is offered digitally. Available download options will depend on the delivery method and access terms specified at checkout.',
  },
  {
    q: 'Is this a one-time payment?',
    a: 'Yes. The advertised price is $27 USD as a one-time payment. There is no monthly subscription associated with this offer.',
  },
  {
    q: 'What happens if the product doesn’t meet my expectations?',
    a: 'Any refund request is subject to the refund policy published for your purchase. Please review the applicable terms before ordering.',
  },
  {
    q: 'Will I receive future updates?',
    a: 'Future updates are not guaranteed unless explicitly stated in the offer terms.',
  },
];

export const buyers = [
  { name: 'Valentina', country: 'Colombia', flag: '🇨🇴' },
  { name: 'Mateo', country: 'Mexico', flag: '🇲🇽' },
  { name: 'Camila', country: 'Argentina', flag: '🇦🇷' },
  { name: 'Sofía', country: 'Chile', flag: '🇨🇱' },
  { name: 'Diego', country: 'Peru', flag: '🇵🇪' },
  { name: 'Lucía', country: 'Spain', flag: '🇪🇸' },
  { name: 'Andrés', country: 'Ecuador', flag: '🇪🇨' },
  { name: 'Isabella', country: 'Uruguay', flag: '🇺🇾' },
  { name: 'Carlos', country: 'Venezuela', flag: '🇻🇪' },
  { name: 'Gabriela', country: 'Costa Rica', flag: '🇨🇷' },
  { name: 'José', country: 'Guatemala', flag: '🇬🇹' },
  { name: 'Mariana', country: 'Dominican Republic', flag: '🇩🇴' },
  { name: 'Fernanda', country: 'Paraguay', flag: '🇵🇾' },
  { name: 'Javier', country: 'Bolivia', flag: '🇧🇴' },
  { name: 'Daniela', country: 'Puerto Rico', flag: '🇵🇷' },
  { name: 'Ana', country: 'Panama', flag: '🇵🇦' },
  { name: 'Ricardo', country: 'Honduras', flag: '🇭🇳' },
  { name: 'Paula', country: 'El Salvador', flag: '🇸🇻' },
];
